import { useState } from "react"
import { Icon } from "../icon/Icon"
import { TIconName } from "../icon/IconUtil"
import { cn } from "@/utils/helpers"
import type { TBanner, TBannerType } from "./types"

type HeroBannerProps = {
  banner: TBanner
  onClose: () => void
}

const TYPE_STYLES: Record<TBannerType, { container: string; link: string }> = {
  info: {
    container: "bg-primary-container text-on-primary-container",
    link: "text-on-primary-container underline",
  },
  success: {
    container: "bg-success-container text-on-success-container",
    link: "text-on-success-container underline",
  },
  warning: {
    container: "bg-warning-container text-on-warning-container",
    link: "text-on-warning-container underline",
  },
  error: {
    container: "bg-error-container text-on-error-container",
    link: "text-on-error-container underline",
  },
}

const TYPE_ICON: Record<TBannerType, TIconName> = {
  info: "info",
  success: "check_circle",
  warning: "warning",
  error: "error",
}

export const HeroBanner = ({ banner, onClose }: HeroBannerProps) => {
  const styles = TYPE_STYLES[banner.type] ?? TYPE_STYLES.info
  const iconName = TYPE_ICON[banner.type] ?? TYPE_ICON.info
  const [isClosing, setIsClosing] = useState(false)

  const handleClose = () => {
    setIsClosing(true)
  }

  const handleTransitionEnd = (
    event: React.TransitionEvent<HTMLDivElement>,
  ) => {
    if (isClosing && event.propertyName === "max-height") {
      onClose()
    }
  }

  return (
    <div
      role="status"
      aria-live="polite"
      onTransitionEnd={handleTransitionEnd}
      className={cn(
        "relative grid overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out",
        isClosing ? "max-h-0 opacity-0" : "max-h-48 opacity-100",
        styles.container,
      )}
    >
      <div className="flex items-center justify-center gap-2 px-12 py-4">
        <Icon icon={iconName} size="sm" />
        <p className="text-md">{banner.text}</p>
        {banner.link && (
          <a
            href={banner.link}
            target="_blank"
            rel="noopener noreferrer"
            className={cn("text-md font-semibold", styles.link)}
          >
            {banner.link_label || banner.link}
          </a>
        )}
      </div>
      <button
        type="button"
        onClick={handleClose}
        aria-label="Close banner"
        className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full hover:bg-black/5"
      >
        <Icon icon="close" size="sm" />
      </button>
    </div>
  )
}
